import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LogType, ObjectDebuggerService } from "blu-utilities";
import {
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from "rxjs";
import { AccountService } from "src/app/modules/authentication/services/account.service";
import { AuthCoordinatorService } from "src/app/modules/authentication/services/auth-coordinator.service";
import {
  getBearerTokenFromRequest,
  getRequestWithBearerToken,
} from "src/app/shared/utils/http-helpers";
import { ErrorMessages } from "../models/error";
import { AppNotificationService } from "../services/app-notification.service";
import { LogoutService } from "../services/logout.service";
import { constructCurlCommand, getHeadersObject } from "../utils/debug";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly CHAMAILEON_ENDPOINT = "tokens/generate";
  private readonly CHAMAILEON_API_KEY = "GewViRHgxGM3clMwPMDd";
  private readonly REFRESH_URL = "/auth/refresh";

  constructor(
    private appNotificationService: AppNotificationService,
    private authCoordinatorService: AuthCoordinatorService,
    private objectDebuggerService: ObjectDebuggerService,
    private accountService: AccountService,
    private logoutService: LogoutService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.accountService.isLoggedIn()) {
      this.logRequestDetails(request);
      return next.handle(request);
    }

    const isChamaileonRequest = request.url.includes(this.CHAMAILEON_ENDPOINT);
    const token = isChamaileonRequest
      ? this.CHAMAILEON_API_KEY
      : this.accountService.getUserAccessToken();
    const processedRequest = token
      ? getRequestWithBearerToken(request, token)
      : request;

    this.logRequestDetails(processedRequest);

    return next.handle(processedRequest).pipe(
      catchError((error) => {
        if (
          this.isRequestEligibleForResend(
            error,
            processedRequest,
            isChamaileonRequest,
          )
        ) {
          return this.handle401Error(processedRequest, next);
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Determines whether the request should be retried with a new token after a 401 error.
   *
   * @param error - The HTTP error response.
   * @param request - The original request.
   * @param isChamaileonRequest - Indicates if the request was for Chamaileon API.
   * @returns `true` if the request should be retried with a new token, otherwise `false`.
   */
  private isRequestEligibleForResend(
    error: any,
    request: HttpRequest<any>,
    isChamaileonRequest: boolean,
  ): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === HttpStatusCode.Unauthorized &&
      !request.url.includes(this.REFRESH_URL) &&
      !isChamaileonRequest
    );
  }

  /**
   * Handles a 401 Unauthorized error by attempting to refresh the access token.
   *
   * @param request - The original HTTP request that failed.
   * @param next - The HTTP request handler.
   * @returns An observable of the HTTP event stream or an error.
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.authCoordinatorService.isRefreshTokenInProgress()) {
      console.warn(
        "TokenInterceptor: Caught 401 RefreshTokenInProgress waitForNewToken.",
      );
      return this.waitForNewToken(request, next);
    }
    const oldToken = getBearerTokenFromRequest(request);
    const newToken = this.accountService.getUserAccessToken();
    return newToken && newToken !== oldToken
      ? next.handle(getRequestWithBearerToken(request, newToken))
      : this.logoutAndThrowError();
  }

  /**
   * Waits for a new token to be issued while a token refresh is in progress.
   *
   * @param request - The original HTTP request that failed.
   * @param next - The HTTP request handler.
   * @returns An observable of the HTTP event stream.
   */
  private waitForNewToken(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.authCoordinatorService.refreshTokenSubject$.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((newToken) =>
        next.handle(getRequestWithBearerToken(request, newToken)),
      ),
    );
  }

  /**
   * Logs out the user and throws an unauthorized error.
   *
   * @returns An observable that throws an error indicating an unauthorized session.
   */
  private logoutAndThrowError(): Observable<never> {
    console.warn("TokenInterceptor: Caught 401 logout And ThrowError.");
    this.logoutService.logout({ redirectToReturnUrl: true }).subscribe();
    this.appNotificationService.showError(ErrorMessages.SESSION_IS_EXPIRED);
    return throwError(() => new Error("Unauthorized, logging out"));
  }

  /**
   * Logs the details of an HTTP request for debugging purposes.
   *
   * @param request - The HTTP request to log.
   */
  private logRequestDetails(request: HttpRequest<any>) {
    this.objectDebuggerService.logObject({
      logType: LogType.REQUEST,
      message: `${request.method} ${request.url}`,
      object: {
        method: request.method,
        url: request.url,
        body: request.body,
        headers: getHeadersObject(request.headers),
        curl_request: constructCurlCommand(
          request,
          this.objectDebuggerService.getSettings(),
        ),
      },
    });
  }
}
